import React, { useCallback, useEffect, useState } from "react";
import { Col, Container, Row, Spinner } from "reactstrap";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import * as Yup from "yup";
import { useFormik } from "formik";
import AuthLogo from "../../Components/Auth/AuthLogo";
import AuthCard from "../../Components/Auth/AuthCard";
import AuthContainer from "../../Components/Auth/AuthContainer";
import CustomInput from "../../Components/Custom/CustomInput";
import CustomButton from "../../Components/Custom/CustomButton";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { BusinessProfileSetupArgs, UserPayload } from "types/auth.api";
import { businessProfileSetup, getUserDetails } from "slices/thunks";
import { redirectPage } from "utils/redirectPage";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
  VALIDATION_PATTERNS,
  createAddressValidation,
  createLocationValidation,
} from "Components/constants/validation";
import Select from "react-select";
import { useDebounce } from "Components/Hooks/useDebounce";
import { getAddressApi } from "helpers/ApiManager";

export interface AddressType {
  postcode: string;
  latitude: number;
  longitude: number;
  formatted_address: string;
  address_line_1: string;
  address_line_2: string;
  address_line_3: string;
  address_line_4: string;
  city: string;
  country: string;
  district: string;
  county: string;
  manually: boolean;
}

export interface AddressOption {
  label: string;
  value: string;
  raw: AddressType;
}

const BusinessAddress = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isLoading, user } = useAppSelector((state) => state?.auth);
  const businessProfileValues = useLocation();
  const [searchPostcode, setSearchPostcode] = useState<string>("");
  const debounceSearchPostcode = useDebounce(searchPostcode, 500);
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSearch = useCallback((value: string) => {
    setSearchPostcode(value);
  }, []);

  const handleClear = useCallback(() => {
    setAddressOptions([]);
  }, []);

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);

    const onBack = () => {
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", onBack);
    return () => window.removeEventListener("popstate", onBack);
  }, []);

  useEffect(() => {
    const fetchPostcodeData = async () => {
      try {
        setLoading(true);
        const res = await getAddressApi(debounceSearchPostcode);
        if (res) {
          console.log("Address Data:", JSON.stringify(res?.payload));
          const options: AddressOption[] = res?.payload?.map(
            (ads: AddressType) => ({
              label: ads.formatted_address,
              value: `${ads.postcode}-${ads.formatted_address}`,
              raw: ads,
            })
          );
          console.log("Address Options:", options);

          setAddressOptions(() => options);
        }
      } catch (error) {
        console.error("Error fetching address data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (debounceSearchPostcode && debounceSearchPostcode.length > 3) {
      fetchPostcodeData();
    }
  }, [debounceSearchPostcode]);

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      streetAddress: "",
      city: "",
      // state: "",
      zipCode: "",
      country: "",
    },
    validationSchema: Yup.object({
      streetAddress: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("your street address"))
        .min(
          VALIDATION_LIMITS.ADDRESS.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Street Address",
            VALIDATION_LIMITS.ADDRESS.MIN
          )
        )
        .max(
          VALIDATION_LIMITS.ADDRESS.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Street Address",
            VALIDATION_LIMITS.ADDRESS.MAX
          )
        )
        .test(
          "valid-address",
          createAddressValidation("Street Address").message,
          createAddressValidation("Street Address").test
        ),
      city: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("your city"))
        .min(
          VALIDATION_LIMITS.LOCATION.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH("City", VALIDATION_LIMITS.LOCATION.MIN)
        )
        .max(
          VALIDATION_LIMITS.LOCATION.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH("City", VALIDATION_LIMITS.LOCATION.MAX)
        )
        .test(
          "valid-city",
          createLocationValidation("City").message,
          createLocationValidation("City").test
        ),
      // state: Yup.string()
      //   .trim()
      //   .required(VALIDATION_MESSAGES.REQUIRED("Your State"))
      //   .min(
      //     VALIDATION_LIMITS.LOCATION.MIN,
      //     VALIDATION_MESSAGES.MIN_LENGTH(
      //       "State",
      //       VALIDATION_LIMITS.LOCATION.MIN
      //     )
      //   )
      //   .max(
      //     VALIDATION_LIMITS.LOCATION.MAX,
      //     VALIDATION_MESSAGES.MAX_LENGTH(
      //       "State",
      //       VALIDATION_LIMITS.LOCATION.MAX
      //     )
      //   )
      //   .test(
      //     "valid-state",
      //     createLocationValidation("State").message,
      //     createLocationValidation("State").test
      //   ),
      zipCode: Yup.string()
        .required(VALIDATION_MESSAGES.REQUIRED("your postcode"))
        .matches(
          VALIDATION_PATTERNS.ZIP_CODE,
          "Postcode must contain only letters, numbers, and spaces"
        )
        .max(
          VALIDATION_LIMITS.ZIP_CODE.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Postcode",
            VALIDATION_LIMITS.ZIP_CODE.MAX
          )
        ),
      country: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("your country"))
        .min(
          VALIDATION_LIMITS.LOCATION.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Country",
            VALIDATION_LIMITS.LOCATION.MIN
          )
        )
        .max(
          VALIDATION_LIMITS.LOCATION.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Country",
            VALIDATION_LIMITS.LOCATION.MAX
          )
        )
        .test(
          "valid-country",
          createLocationValidation("Country").message,
          createLocationValidation("Country").test
        ),
    }),
    onSubmit: async (values) => {
      const businessPayload: BusinessProfileSetupArgs = {
        address: values?.streetAddress?.trim(),
        city: values?.city?.trim(),
        // state: values.state?.trim(),
        postcode: values?.zipCode?.toString()?.trim(),
        country: values.country?.trim(),
        // email: user?.email!,
        name: businessProfileValues?.state?.businessName?.trim(),
        phone: businessProfileValues?.state?.phone?.trim(),
        logo: businessProfileValues?.state?.selectedFile,
        brand_color: businessProfileValues?.state?.brand_color,
        vat_number: businessProfileValues?.state?.vatNumber?.trim(),
        vertical_market_id: businessProfileValues?.state?.trade,
      };

      try {
        const res = await dispatch(
          businessProfileSetup(businessPayload)
        )?.unwrap();
        const updatedUser: UserPayload = {
          ...(user as UserPayload),
          company: res?.payload,
          is_company_profile_setup: true,
        };
        const route = redirectPage(updatedUser, false);
        if (route) {
          navigate(route, { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
        dispatch(getUserDetails());
      } catch (error) {}
    },
  });

  document.title = "Business Profile Setup | EaziQuote";
  return (
    <React.Fragment>
      <ParticlesAuth>
        <AuthContainer>
          <Container>
            <Row>
              <Col lg={12}>
                <AuthLogo />
              </Col>
            </Row>

            <Row className="auth-container">
              <Col md={8} lg={6} xl={5}>
                <AuthCard>
                  <div className="text-center mt-2">
                    <h5 className="text-black2D font-primary fw-semibold fs-24">
                      Business Address
                    </h5>
                  </div>
                  <div className="p-2">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                      }}
                    >
                      <Col md="12" className="mb-3 mt-2">
                        <div className="flex-fill field-inline">
                          <Select
                            value={searchPostcode}
                            inputValue={searchPostcode}
                            filterOption={() => true}
                            onChange={(
                              selectedOption: AddressOption,
                              actionMeta: any
                            ) => {
                              if (actionMeta.action === "clear") {
                                handleClear();
                                return;
                              }

                              if (!selectedOption) {
                                return;
                              }

                              const rawData = selectedOption?.raw;

                              const fullStreetAddress = [
                                rawData.address_line_1,
                                rawData.address_line_2,
                                rawData.address_line_3,
                                rawData.address_line_4,
                              ]
                                .map((line) => line?.trim())
                                .filter(Boolean)
                                .join(", ");

                              validation.resetForm();

                              validation.setFieldValue(
                                "streetAddress",
                                fullStreetAddress
                              );
                              validation.setFieldValue("city", rawData.city);
                              validation.setFieldValue(
                                "zipCode",
                                rawData.postcode
                              );
                              validation.setFieldValue(
                                "country",
                                rawData.country
                              );

                              setSearchPostcode("");
                              setAddressOptions([]);
                            }}
                            onInputChange={(inputValue: any) => {
                              handleSearch(inputValue);
                            }}
                            options={addressOptions}
                            placeholder="Search postcode"
                            classNamePrefix="react-select"
                            className={`auth-form-dropdown-control`}
                            isLoading={loading}
                            loadingMessage={() => (
                              <Spinner size="sm" color="primary" />
                            )}
                            noOptionsMessage={({
                              inputValue,
                            }: {
                              inputValue: any;
                            }) =>
                              inputValue
                                ? `No postcode found for "${inputValue}"`
                                : "No postcode available"
                            }
                            isClearable
                            styles={{
                              menuPortal: (base: any) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                              control: (base: any) => ({
                                ...base,
                                borderColor: base.borderColor,
                                backgroundColor: "#fff",
                                "&:hover": {
                                  borderColor: base.borderColor,
                                },
                              }),
                            }}
                          />
                        </div>
                      </Col>

                      <CustomInput
                        label="Street Address"
                        placeholder="Street address"
                        name="streetAddress"
                        type="text"
                        value={validation.values.streetAddress}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.streetAddress}
                        error={validation.errors.streetAddress}
                      />

                      <CustomInput
                        label="City"
                        placeholder="City"
                        name="city"
                        type="text"
                        value={validation.values.city}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.city}
                        error={validation.errors.city}
                      />

                      <CustomInput
                        label="Postcode"
                        placeholder="Postcode"
                        name="zipCode"
                        type="text"
                        value={validation.values.zipCode}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.zipCode}
                        error={validation.errors.zipCode}
                      />

                      <CustomInput
                        label="Country"
                        placeholder="Country"
                        name="country"
                        type="text"
                        value={validation.values.country}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        touched={validation.touched.country}
                        error={validation.errors.country}
                      />
                      <CustomButton
                        text="Continue"
                        type="submit"
                        loading={isLoading}
                        className="auth-Button"
                      />
                    </form>
                  </div>
                </AuthCard>
              </Col>
            </Row>
          </Container>
        </AuthContainer>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default BusinessAddress;
